import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { setUserId, ga4 } from '@/lib/gtag';
import { safeLog, safeError } from '@/lib/safeLog';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  onboardingStatus: 'not_started' | 'in_progress' | 'completed' | null;
  signOut: () => Promise<void>;
  handleSessionError: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [onboardingStatus, setOnboardingStatus] = useState<'not_started' | 'in_progress' | 'completed' | null>(null);

  // Clear all auth-related storage
  const clearAuthStorage = useCallback(() => {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('supabase') || key.includes('sb-'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (e) {
      safeError('Error clearing auth storage:', e);
    }
  }, []);

  // Sign out and redirect
  const signOut = useCallback(async () => {
    try {
      clearAuthStorage();
      await supabase.auth.signOut();
    } catch (e) {
      safeError('Sign out error:', e);
    } finally {
      setUser(null);
      setSession(null);
    }
  }, [clearAuthStorage]);

  // Handle session errors (called when edge functions return SESSION_NOT_FOUND)
  const handleSessionError = useCallback(async () => {
    safeLog('Session error detected, signing out...');
    await signOut();
    // Navigation will be handled by the component or the caller
  }, [signOut]);

  // Validate session server-side
  const validateSession = useCallback(async () => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        if (user) {
          // Had a user but no session - session expired
          await signOut();
        }
        return false;
      }

      // Validate with server using getUser (makes API call)
      const { data: { user: validUser }, error } = await supabase.auth.getUser();
      
      if (error || !validUser) {
        safeLog('Session validation failed:', error?.message);
        await signOut();
        return false;
      }

      return true;
    } catch (e) {
      safeError('Session validation error:', e);
      return false;
    }
  }, [user, signOut]);

  // Initial setup - listen for auth changes
  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        safeLog('Auth state changed:', event);
        
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
          clearAuthStorage();
          // Clear GA4 user ID on sign out
          setUserId(null);
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          // B4: always clear loading on SIGNED_IN so the Auth page never hangs
          setIsLoading(false);
          // Set GA4 user ID and track login
          if (currentSession?.user?.id) {
            setUserId(currentSession.user.id);
            if (event === 'SIGNED_IN') {
              ga4.login('email');
              
              // Trigger migration for new users who signed in via magic link
              // Only runs if migration hasn't been triggered yet (migration_triggered_at is null)
              const userId = currentSession.user.id;
              const userEmail = currentSession.user.email;
              if (userEmail) {
                supabase
                  .from('profiles')
                  .select('migration_triggered_at')
                  .eq('id', userId)
                  .single()
                  .then(({ data: profile }) => {
                    if (!profile?.migration_triggered_at) {
                      const migratorApiUrl = import.meta.env.VITE_MIGRATOR_API_URL;
                      supabase.functions.invoke('trigger-migration', {
                        body: { email_address: userEmail, reboot_user_id: userId, migrator_api_url: migratorApiUrl },
                      }).then(({ data, error }) => {
                        if (error) {
                          safeError('Failed to trigger migration:', error);
                          return;
                        }
                        if (data?.success) {
                          supabase.from('profiles').update({ migration_triggered_at: new Date().toISOString() }).eq('id', userId);
                        } else {
                          safeError('Migration returned failure:', data?.error);
                        }
                      }).catch(err => {
                        safeError('Failed to trigger migration:', err);
                      });
                    }
                  });
              }
            }
          }
        } else if (event === 'INITIAL_SESSION') {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          setIsLoading(false);
          // Set GA4 user ID for existing session
          if (currentSession?.user?.id) {
            setUserId(currentSession.user.id);
          }
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      // If a magic-link hash is present and we don't yet have a session,
      // wait for the INITIAL_SESSION event to flip isLoading. Otherwise
      // route guards will bounce the user before the token is exchanged.
      const hash = typeof window !== 'undefined' ? window.location.hash : '';
      const pendingMagicLink =
        !existingSession &&
        (hash.includes('access_token=') || hash.includes('type=magiclink') || hash.includes('type=recovery'));
      if (!pendingMagicLink) {
        setIsLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [clearAuthStorage]);

  // Periodic session validation
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      validateSession();
    }, SESSION_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [user, validateSession]);

  // Fetch onboarding status once when user changes
  useEffect(() => {
    if (user?.id) {
      supabase
        .from('profiles')
        .select('onboarding_status')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          setOnboardingStatus(data?.onboarding_status ?? 'not_started');
        });
    } else {
      setOnboardingStatus(null);
    }
  }, [user?.id]);

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user && !!session,
    onboardingStatus,
    signOut,
    handleSessionError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
